import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Typography, Box, Input } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DriverFolder from "../components/DriverFolder";
import { useAuth } from "react-oidc-context";
import { useCustomAuth } from "../contexts/AuthContext";
import { ANNOTATION_SESSION_ENDPOINT } from "../utils/constants";
import { traverseItems, extractMidiFromZip } from "../utils/uploadMidi";
import { UserProfile } from "../components/UserProfile";
import NeuroMuseLogo from "../assets/img/logo/NeuroMuseLogo.svg"; // Reverted to colorful SVG logo
import SearchBarWithDropdown from "../components/SearchBarWithDropdown";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import { authFetch } from "../utils/authFetch";
import LoadingModal from "../components/LoadingModal";
import { uploadMidiWithDriver } from "../utils/uploadDriver";
import UploadStatusPopover from "../components/UploadStatus";
import { normalizeUserAnnotatorAssignments } from "../utils/annotatorAssignment";

const Home = ({ handleLogout }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { userData, setUserData, isAdminMode } = useCustomAuth();
  const [refreshToggle, setRefreshToggle] = useState(false);
  const userName = auth.user?.profile?.name || "User";

  const [currentPath, setCurrentPath] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const driverFolderRef = useRef(null);
  const [rootNode, setRootNode] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  // upload related states
  const [uploadStatusOpen, setUploadStatusOpen] = useState(false);
  const [uploadStats, setUploadStats] = useState({});
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);

  // Helper to traverse DriverFolder's rootNode and collect matches
  const collectMatches = (node, query, path = []) => {
    if (!node) return [];
    let results = [];
    for (const [name, child] of Object.entries(node.children || {})) {
      const fullPath = [...path, name];
      if (name.toLowerCase().includes(query.toLowerCase())) {
        results.push({
          name,
          path: fullPath,
          sheet_id: child.sheet_id,
          unique_id: fullPath.join("/") + (child.sheet_id || ""),
          type: child.sheet_id ? "file" : "folder"
        });
      }
      results = results.concat(collectMatches(child, query, fullPath));
    }
    return results;
  };

  // Update searchResults as user types or when rootNode changes
  useEffect(() => {
    if (!searchInput || !rootNode) {
      setSearchResults([]);
      return;
    }
    const results = collectMatches(rootNode, searchInput);
    // Sort: folders first, then files; both alphabetized
    results.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === "folder" ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
    setSearchResults(results);
  }, [searchInput, rootNode]);

  // Patch DriverFolder to expose rootNode via ref
  useEffect(() => {
    if (driverFolderRef.current) {
      driverFolderRef.current.rootNode = driverFolderRef.current.getRootNode && driverFolderRef.current.getRootNode();
    }
  }, []);

  const getIcon = (item) => item.type === "folder" ? <FolderIcon /> : <DescriptionIcon />;

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.profile?.email) return;
    const email = auth.user.profile.email;
    authFetch(
      `${ANNOTATION_SESSION_ENDPOINT}/getUser?user_id=${encodeURIComponent(email)}`,
      {},
      auth.user?.id_token
    )
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch user data");
        return res.json();
      })
      .then((data) => {
        // console.log("refreshed userData:", data);
        setUserData(normalizeUserAnnotatorAssignments(data));
      })
      .catch((err) => console.error("Error refreshing user data:", err));
  }, [auth.isAuthenticated, auth.user, setUserData]);

  const handlePathChange = useCallback((newPath) => {
    setCurrentPath(newPath);
    setSearchTerm("");
    setSearchInput("");
    // push the new “?path=” into the URL whenever you navigate folders
    const search = newPath.length
      ? `?path=${encodeURIComponent(newPath.join("/"))}`
      : "";
    navigate({ pathname: "/", search }, { replace: false });
  }, []);

  const resetPath = () => {
    setCurrentPath([]);
    setSearchTerm("");
    setSearchInput("");
    if (driverFolderRef.current) {
      driverFolderRef.current.resetPath();
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const getFolderTitle = () =>
    searchTerm
      ? "Search Results"
      : currentPath.length === 0
      ? "All Folders"
      : currentPath[0];

  const [loading, setLoading] = useState(false);

  const handleDrop = async (event) => {
    event.preventDefault();
    if (searchTerm) {
      return;
    }
    const currentFolder = currentPath.length === 0 ? "" : currentPath.join("/");
    const userId = userData.user_id;
    const items = Array.from(event.dataTransfer.items || []);
    const files = Array.from(event.dataTransfer.files || []);
    const midiFiles = [];

    const zipFiles = files.filter((f) => f.name.toLowerCase().endsWith(".zip"));
    for (const zipFile of zipFiles) {
      const extracted = await extractMidiFromZip(zipFile);
      midiFiles.push(...extracted);
    }

    const filesWithPaths = await traverseItems(items);
    const folderEntries = filesWithPaths.filter(({ file }) =>
      /\.(mid|midi)$/i.test(file.name)
    );
    midiFiles.push(...folderEntries);

    const looseMidiFiles = files
      .filter(
        (file) =>
          /\.(mid|midi)$/i.test(file.name) && !/^[_\.]/.test(file.name)
      )
      .map((file) => ({
        file,
        relativePath: currentFolder ? file.name : `new_folder/${file.name}`,
      }));
    midiFiles.push(...looseMidiFiles);

    if (midiFiles.length === 0) {
      alert("No MIDI files found.");
      return;
    }

    try {
      const result = await uploadMidiWithDriver({
        midiFiles,
        userId,
        ...(currentFolder && { folder: currentFolder }),
        onLoadingChange: setLoading,
      });
      setUploadStats({
        total: result.total,
        success: result.success.length,
        deleted: result.deleted.length,
        failed: result.failed.length,
        errors: result.errors || [],
      });
      setDeletedFiles(result.deleted || []);
      setFailedFiles(result.failed || []);
      setUploadStatusOpen(true);
    } catch (e) {
      console.error("uploadMidiWithDriver encountered error: ", e);
      alert('UploadDriver encountered an error: ', e);
    }
    setRefreshToggle(r => !r);
    // “re-navigate” with the same folder in the query string:
    const folder = currentPath.join("/");
    navigate(`/${folder ? `?path=${encodeURIComponent(folder)}` : ""}`, { replace: true });
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
      }}
      onDragOver={isAdminMode ? handleDragOver : undefined}
      onDrop={isAdminMode ? handleDrop : undefined}
    >
      {loading && <LoadingModal message={"Uploading files… (正在上传文件...)"}/>}

      <UploadStatusPopover
        open={uploadStatusOpen}
        onClose={() => setUploadStatusOpen(false)}
        stats={uploadStats}
        deletedFiles={deletedFiles}
        failedFiles={failedFiles}
      />

      <Sidebar resetPath={resetPath} />

      <Box sx={{ 
        marginLeft: "200px", 
        flexGrow: 1, 
        display: "flex", 
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden"
      }}>
        {/* Top Navigation Bar - Fixed */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          py: 2, 
          px: 3, 
          borderBottom: "1px solid #eaeaea",
          backgroundColor: "white",
          zIndex: 1000,
          flexShrink: 0
        }}>
          {/* Logo (NeuroMuse AI) - Left */}
          <Box sx={{ flexBasis: "250px" }}>
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <img
                src={NeuroMuseLogo}
                alt="NeuroMuse Logo"
                style={{ height: "40px", cursor: "pointer" }}
                onClick={() => {
                  resetPath();
                  navigate("/");
                }}
              />
            </Box>
          </Box>

          {/* Search Bar - Centered, shifted right */}
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", maxWidth: "850px", mx: 13 }}>
            <SearchBarWithDropdown
              value={searchInput}
              onChange={setSearchInput}
              onSelect={item => {
                setSearchInput(item.name);
                setSearchTerm(item.name);
              }}
              onEnter={() => setSearchTerm(searchInput)}
              results={searchResults}
              getIcon={getIcon}
            />
          </Box>

          {/* User Profile - Right */}
          <Box sx={{ ml: "auto" }}>
            <UserProfile handleLogout={handleLogout} userName={userName} />
          </Box>
        </Box>

        {/* Scrollable Content Area */}
        <Box sx={{ 
          flexGrow: 1, 
          overflow: "auto",
          backgroundColor: "#fafafa"
        }}>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Folder Title*/}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
              <Typography variant="h6" fontWeight="bold">
                {getFolderTitle()}
              </Typography>
            </Box>

            {/* Driver Folder Component */}
            <DriverFolder ref={driverFolderRef} onPathChange={handlePathChange} searchTerm={searchTerm} isAdminMode={isAdminMode} setRootNode={setRootNode} refreshToggle={refreshToggle}/>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
